import React, { useState } from "react";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { skills, projects } from "../data/lists";

import "../styles/homeStyles.css";
import profilePic from "../assets/pp1_noBG.png"; // Import the image
import NetworkBackground from "../components/NetworkBackground";

const renderProjectDescription = (description: string) => {
  const blocks: JSX.Element[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    const text = paragraphLines.join(" ").trim();

    if (text) {
      blocks.push(<p key={`paragraph-${blocks.length}`}>{text}</p>);
    }

    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length) {
      blocks.push(
        <ul key={`list-${blocks.length}`} className="project-description-list">
          {listItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>,
      );
    }

    listItems = [];
  };

  for (const rawLine of description.split(/\r?\n/)) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.*)$/);

    if (bulletMatch) {
      flushParagraph();
      listItems.push(bulletMatch[1]);
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();

  return blocks;
};

const LAST_UPDATED = "May 7, 2026";

const HomeScreen = () => {
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  const handleProjectClick = (projectName: string) => {
    if (expandedProject === projectName) {
      setExpandedProject(null);
    } else {
      setExpandedProject(projectName);
    }
  };

  return (
    <div>
      <div className="network-bg">
        <NetworkBackground />
      </div>
      {/* Header */}
      <header className="header">
        <h3>Koen Schoffner</h3>
        <nav className="nav">
          <a href="#about" className="nav-link">
            About
          </a>
          <a href="#currently-working" className="nav-link">
            Currently Working On
          </a>
          <a href="#skills" className="nav-link">
            Skills
          </a>
          <a href="#projects" className="nav-link">
            Projects
          </a>
        </nav>
      </header>

      {/* Main Content */}
      <main className="main">
        {/* Hero Section */}
        <section className="section hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Hi, I'm Koen</h1>
              <p>
                I'm a highly motivated software engineer with a hunger to learn
                and expand into new fields.
              </p>
              <a href="#projects" className="button">
                View My Work
              </a>
            </div>
            {/* Use the imported image */}
            <div className="profile-container">
              <img src={profilePic} alt="Profile" className="profile-pic" />
              <div className="social-icons">
                <a
                  href="https://github.com/Kschoffner13"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaGithub />
                </a>
                <a
                  href="https://www.linkedin.com/public-profile/settings?trk=d_flagship3_profile_self_view_public_profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLinkedin />
                </a>
                <span className="email-text">kschoffner@gmail.com</span>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="section section-half align-right">
          <h2>About Me</h2>
          <p>
            My journey has taken me from playing semi-professional soccer in
            Germany, where I became fluent in German, to pursuing a software
            engineering degree at the University of Calgary. As a two-time
            Academic All-Canadian athlete, I've always balanced a competitive
            drive with a passion for learning. Now in my final year, I'm focused
            on my capstone project and exploring hobbies like computer building
            and 3D modeling.
          </p>
        </section>

        {/* Currently Working On Section */}
        <section
          id="currently-working"
          className="section section-half align-left"
        >
          <h2>Currently Working On</h2>
          <p>
            Right now, I’m working on a mix of projects — some aimed at
            deepening my knowledge of modern frameworks and data platforms like
            Databricks, and others focused on building real products that
            combine my passion for software development and sport. Whether it’s
            exploring new tooling or shipping something that makes an impact on
            the field, I’m always looking for ways to grow as an engineer while
            building things I genuinely care about.
          </p>
        </section>

        {/* Skills Section */}
        <section id="skills" className="section section-half align-right">
          <h2>Skills</h2>
          <ul className="skills-list">
            {skills.map((skill) => (
              <li key={skill} className="skill-item">
                {skill}
              </li>
            ))}
          </ul>
        </section>

        {/* Projects Section */}
        <section id="projects" className="section">
          <h2>Projects</h2>
          <div className="project-grid">
            {projects.map((project) => (
              <div
                key={project.name}
                className={`project-card ${
                  expandedProject === project.name ? "expanded" : ""
                }`}
                onClick={() => handleProjectClick(project.name)}
              >
                <div className="project-header">
                  <div className="project-title-status">
                    <h3>{project.name}</h3>
                    <span
                      className={`status-tag status-${project.status
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {project.status}
                    </span>
                    {project.new && <span className="new-tag">New</span>}
                  </div>
                  <ul className="project-skills-list">
                    {project.skills.map((skill) => (
                      <li key={skill} className="skill-item">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="project-body-wrapper">
                  <div className="project-body">
                    {renderProjectDescription(project.description)}
                    {project.link && (
                      <a href={project.link} className="button">
                        View Project
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        <footer className="footer">
          <p className="footer-meta">Last updated {LAST_UPDATED}</p>
          <p>&copy; 2026 Koen Schoffner. All Rights Reserved.</p>
        </footer>
      </main>

      {/* Footer */}
    </div>
  );
};

export default HomeScreen;
